// src/hooks/useProductFilters.ts

import { useState, useEffect, useCallback } from 'react';

interface UseProductFiltersProps {
    fetchProducts: (page: number, status: string, search: string) => Promise<{
        data: any[];
        total: number;
        pages: number;
    }>;
    initialStatus?: string;
}

export const useProductFilters = ({ fetchProducts, initialStatus = 'all' }) => {
    const [statusFilter, setStatusFilter] = useState(initialStatus);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState<any[]>([]);
    const [pagination, setPagination] = useState({
        page: 1,
        total: 0,
        pages: 0,
        pageSize: 20,
    });

    const fetchData = useCallback(async (newPage?: number, silent = false) => {
        const currentPage = typeof newPage === 'number' ? newPage : (typeof page === 'number' ? page : 1);
        if (!silent) setLoading(true);
        try {
            const response = await fetchProducts(currentPage, statusFilter, searchTerm);
            setProducts(response.data || []);
            setPagination({
                page: currentPage,
                total: response.total || 0,
                pages: response.pages || 0,
                pageSize: 20,
            });
        } catch (error) {
            console.error('Failed to fetch products:', error);
            setProducts([]);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [page, statusFilter, searchTerm, fetchProducts]);

    // ✅ Fetch when statusFilter changes
    useEffect(() => {
        setPage(1);
        fetchData(1);
    }, [statusFilter]);

    // ✅ Fetch when searchTerm changes (debounced in parent)
    useEffect(() => {
        if (searchTerm !== undefined) {
            setPage(1);
            fetchData(1);
        }
    }, [searchTerm]);

    const handleStatusFilterChange = (filter: string) => {
        setStatusFilter(filter);
        setSearchTerm('');
    };

    const handleSearchChange = (search: string) => {
        setSearchTerm(search);
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        fetchData(newPage);
    };

    return {
        products,
        setProducts,
        loading,
        pagination,
        statusFilter,
        searchTerm,
        handleStatusFilterChange,
        handleSearchChange,
        handlePageChange,
        fetchData,
    };
};